import React from "react";
import { Form, Button } from "react-bootstrap";
import DatePicker from "./DatePicker/DatePicker";
import { parseISO, format as formatDate } from "date-fns";

export function Field(props) {
    const {
        config,
        value: propValue,
        error,
        onChange,
        onBlur,
        onFocus,
    } = props;

    // Priorizar config.value sobre propValue
    const value = config.value !== undefined ? config.value : propValue;

    // Função para obter o valor com máscara aplicada
    const getDisplayValue = () => {
        const stringValue = String(value ?? "");
        if (config.mask && stringValue) {
            return config.mask(stringValue);
        }
        return stringValue;
    };

    // Classes CSS base - Ajustadas para Bootstrap
    const baseInputClasses = `form-control
    ${error ? "is-invalid" : ""}`;

    const labelClasses = `form-label fw-semibold ${
        config.labelClassName || ""
    }`;

    // date popup handled by the custom DatePicker component

    // Função para lidar com mudanças de valor
    const handleChange = (newValue) => {
        let processedValue = newValue;

        // Aplicar máscara se existir
        if (config.mask && typeof newValue === "string") {
            processedValue = config.mask(newValue);
        }

        // Se existe onChange personalizado no config, usa ele em vez do padrão
        if (config.onChange) {
            config.onChange(processedValue);
        } else {
            onChange(processedValue);
        }
    };

    // Função para lidar com blur
    const handleBlur = () => {
        // Manter a máscara visível no campo, não aplicar unmask no blur
        // O unmask deve ser aplicado apenas quando necessário (ex: envio de dados)
        onBlur?.();
        config.onBlur?.(value);
    };

    // Função para lidar com focus
    const handleFocus = () => {
        onFocus?.();
        config.onFocus?.(value);
    };

    // Renderizar label com ícone
    const renderLabel = () => {
        if (config.hide) return null;
        return (
            <label className={labelClasses}>
                {config.icon && (
                    <config.icon
                        className="me-2"
                        style={{ width: "1rem", height: "1rem" }}
                    />
                )}
                {config.label}
                {config.required && <span className="text-danger"> *</span>}
            </label>
        );
    };

    // Renderizar erro
    const renderError = () => {
        if (!error || config.hide) return null;
        return (
            <div className="invalid-feedback d-flex align-items-center">
                <svg
                    className="me-1"
                    style={{ width: "1rem", height: "1rem" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                >
                    <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                    />
                </svg>
                {error}
            </div>
        );
    };

    // Renderizar campo com botão de ação
    const renderFieldWithAction = (field) => {
        if (!config.actionButton) return field;

        return (
            <div className="input-group">
                {field}
                <button
                    type="button"
                    onClick={() => config.actionButton?.onClick(value)}
                    disabled={
                        config.actionButton.disabled ||
                        config.actionButton.loading
                    }
                    className="btn btn-outline-secondary"
                >
                    <config.actionButton.icon
                        style={{ width: "1.25rem", height: "1.25rem" }}
                        className={
                            config.actionButton.loading
                                ? "spinner-border spinner-border-sm"
                                : ""
                        }
                    />
                </button>
            </div>
        );
    };

    // Renderizar campo baseado no tipo
    const renderField = () => {
        if (config.hide) return null;

        const inputClasses = `${baseInputClasses} ${
            config.inputClassName || ""
        }`;
        const displayValue = getDisplayValue(); // Usar valor com máscara aplicada

        // Configurar autocomplete - por padrão "off", mas permite personalização
        const getAutocomplete = () => {
            if (config.autocomplete === false) return "off";
            if (config.autocomplete) return config.autocomplete;

            // Padrões específicos por tipo de campo para melhor UX
            // Alguns navegadores ignoram "off", então usamos valores "falsos" mais eficazes
            switch (config.type) {
                case "email":
                    return "nope"; // Chrome ignora "off" mas respeita valores inválidos
                case "password":
                    return "new-password"; // Força o navegador a não sugerir senhas existentes
                default:
                    return "off"; // Para outros campos, "off" geralmente funciona
            }
        };

        switch (config.type) {
            case "text":
            case "email":
            case "password":
            case "tel":
            case "url":
            case "search":
                return renderFieldWithAction(
                    <input
                        type={config.type}
                        value={displayValue}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        placeholder={config.placeholder}
                        disabled={config.disabled}
                        readOnly={config.readOnly}
                        minLength={config.minLength}
                        maxLength={config.maxLength}
                        pattern={config.pattern}
                        autoComplete={getAutocomplete()}
                        data-form-type="other"
                        data-lpignore="true"
                        className={inputClasses}
                    />
                );

            case "number":
                return renderFieldWithAction(
                    <input
                        type="number"
                        value={displayValue}
                        onChange={(e) => handleChange(Number(e.target.value))}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        placeholder={config.placeholder}
                        disabled={config.disabled}
                        readOnly={config.readOnly}
                        min={config.min}
                        max={config.max}
                        step={config.step}
                        autoComplete={getAutocomplete()}
                        className={inputClasses}
                    />
                );

            case "textarea":
                return (
                    <textarea
                        value={displayValue}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        placeholder={config.placeholder}
                        disabled={config.disabled}
                        readOnly={config.readOnly}
                        rows={config.rows || 4}
                        cols={config.cols}
                        minLength={config.minLength}
                        maxLength={config.maxLength}
                        className={inputClasses}
                    />
                );

            case "select":
                return (
                    <Form.Select
                        value={displayValue}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        disabled={config.disabled}
                        className={error ? "is-invalid" : ""}
                    >
                        {config.placeholder && (
                            <option value="">{config.placeholder}</option>
                        )}
                        {config.options?.map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                            >
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                );

            case "checkbox":
                return (
                    <div
                        className="form-check d-flex h-100 align-items-center"
                        style={{ marginTop: 30 }}
                    >
                        <Form.Check
                            type="checkbox"
                            checked={Boolean(value)}
                            onChange={(e) => handleChange(e.target.checked)}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                            disabled={config.disabled}
                            className={`me-3 pb-2 ${error ? "is-invalid" : ""}`}
                        />
                        {config.label && renderLabel()}
                    </div>
                );

            case "radio":
                return (
                    <div
                        className={`d-flex ${
                            config.radioOrientation === "vertical"
                                ? "flex-column  align-items-start"
                                : "flex-row  align-items-center"
                        }`}
                    >
                        {config.options?.map((option) => (
                            <Form.Check
                                key={option.value}
                                type="radio"
                                id={`${config.name}-${option.value}`}
                                name={config.name}
                                value={option.value}
                                checked={String(value) === option.value}
                                onChange={(e) => {
                                    handleChange(e.target.value);
                                }}
                                onBlur={handleBlur}
                                onFocus={handleFocus}
                                disabled={config.disabled}
                                label={option.label}
                                className={`me-3 ${error ? "is-invalid" : ""}`}
                            />
                        ))}
                    </div>
                );

            case "button":
                return (
                    <div className="d-flex align-items-end">
                        <Button
                            variant={config.buttonConfig?.variant || "primary"}
                            size={config.buttonConfig?.size || "md"}
                            type={config.buttonConfig?.type || "button"}
                            className={config.buttonConfig?.className || ""}
                            disabled={
                                config.buttonConfig?.disabled ||
                                config.buttonConfig?.isLoading
                            }
                            onClick={
                                config.buttonConfig?.onClick
                                    ? config.buttonConfig.onClick
                                    : undefined
                            }
                        >
                            {config.buttonConfig?.isLoading && (
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                            )}
                            {config.buttonConfig?.icon &&
                                !config.buttonConfig?.isLoading && (
                                    <config.buttonConfig.icon className="me-2" />
                                )}
                            {config.buttonConfig?.label || "Button"}
                        </Button>
                    </div>
                );

            case "date": {
                // Convert stored value (string) to Date for the DatePicker component
                const valueDate = value
                    ? typeof value === "string"
                        ? // try parse ISO, fallback to Date constructor
                          (() => {
                              try {
                                  return parseISO(value);
                              } catch (e) {
                                  return new Date(value);
                              }
                          })()
                        : value instanceof Date
                        ? value
                        : new Date(value)
                    : null;

                return renderFieldWithAction(
                    <DatePicker
                        id={config.name}
                        label={config.placeholder || config.label}
                        value={valueDate}
                        position={config.position || "right"}
                        onChange={(d) => {
                            if (!d) return handleChange("");
                            // normalize to yyyy-MM-dd string
                            try {
                                handleChange(formatDate(d, "yyyy-MM-dd"));
                            } catch (e) {
                                handleChange(d.toISOString());
                            }
                        }}
                    />
                );
            }

            case "datetime-local":
            case "time":
                return renderFieldWithAction(
                    <input
                        type={config.type}
                        value={displayValue}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        disabled={config.disabled}
                        readOnly={config.readOnly}
                        min={config.min ? String(config.min) : undefined}
                        max={config.max ? String(config.max) : undefined}
                        autoComplete={getAutocomplete()}
                        className={`${inputClasses} date-input`}
                    />
                );

            case "file":
                return (
                    <input
                        type="file"
                        onChange={(e) => {
                            const files = e.target.files;
                            handleChange(
                                config.multiple
                                    ? Array.from(files || [])
                                    : files?.[0] || null
                            );
                        }}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        disabled={config.disabled}
                        accept={config.accept}
                        multiple={config.multiple}
                        className={inputClasses}
                    />
                );

            default:
                return (
                    <input
                        type="text"
                        value={displayValue}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        placeholder={config.placeholder}
                        disabled={config.disabled}
                        readOnly={config.readOnly}
                        autoComplete={getAutocomplete()}
                        className={inputClasses}
                    />
                );
        }
    };

    return (
        <div className={`d-flex flex-column h-100 ${config.className || ""}`}>
            {config.label && config.type !== "checkbox" && renderLabel()}
            {renderField()}
            {renderError()}
        </div>
    );
}
